/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { TextDimensions, useTextDimensions } from '../../hooks/PRIVATE_useTextDimensions';
import { isHeightOverlap, isWidthOverlap } from '../../utils/UNSAFE_visUtils';
import { AxisPosition, Dimension } from '../axis.types';
import { AXIS_DEFAULTS } from './axisDefaults';

type GetTextDimensions = NonNullable<ReturnType<typeof useTextDimensions>['getTextDimensions']>;
/**
 * Calculates the number of skips between axis labels and the dimensions of unskipped labels.
 * @param labels The labels to render.
 * @param getLabelDims The function that returns dimension of given label.
 * @param isHoriz Whether the axis is horizontal.
 * @param numOfSafeSkips The no of labels to skip safely.
 * @returns
 */
export function skipLabelsUniform(
  labels: string[],
  getLabelDims: (label: string, index: number) => Dimension,
  isHoriz: boolean,
  numOfSafeSkips?: number
) {
  let numOfSkips = numOfSafeSkips || 0;
  let dimsA: TextDimensions;
  let dimsB: TextDimensions;
  const dims: TextDimensions[] = [getLabelDims(labels[0], 0)];
  const isOverlapping = isHoriz ? isWidthOverlap : isHeightOverlap;

  let indexA = 0;
  let indexB = numOfSkips + 1;
  while (indexB < labels.length) {
    dimsA = dims[indexA] || getLabelDims(labels[indexA], indexA);
    if (!dims[indexA]) dims[indexA] = dimsA;
    dimsB = dims[indexB] || getLabelDims(labels[indexB], indexB);
    if (!dims[indexB]) dims[indexB] = dimsB;
    if (isOverlapping(dimsA, dimsB)) {
      numOfSkips += 1;
      indexA = 0;
      indexB = numOfSkips + 1;
    } else {
      indexA = indexB;
      indexB = indexA + numOfSkips + 1;
    }
  }
  return { numOfSkips, dims };
}

/**
 * Greedily skip labels. We will skip next label if it overlaps with the previous one.
 * This might not result in uniformly spaced labels.
 * @param labels
 * @param getLabelDims
 * @param isHoriz
 * @returns
 */
export function skipLabelsGreedy(
  labels: string[],
  getLabelDims: (label: string, index: number) => Dimension,
  isHoriz: boolean
) {
  const isOverlapping = isHoriz ? isWidthOverlap : isHeightOverlap;
  let currentDims = getLabelDims(labels[0], 0);

  const renderedLabels = [
    {
      label: labels[0],
      index: 0,
      labelProps: currentDims
    }
  ];
  let maxWidth = currentDims.width;
  let maxHeight = currentDims.height;
  for (let j = 1; j < labels.length; j++) {
    if (!labels[j]) continue;
    const labelDim = getLabelDims(labels[j], j);
    if (!isOverlapping(currentDims, getLabelDims(labels[j], j))) {
      currentDims = labelDim;
      renderedLabels.push({
        label: labels[j],
        index: j,
        labelProps: labelDim
      });

      maxWidth = Math.max(maxWidth, labelDim.width);
      maxHeight = Math.max(maxHeight, labelDim.height);
    }
  }
  return { tickLabels: renderedLabels, maxWidth, maxHeight };
}

/**
 * Retuns the max dimension of the rendered labels after skipping overlapping labels.
 * @param labels An array of labels.
 * @param getLabelDims The function called to get dimension of the given label.
 * @param isHoriz If the overlap be measured in horizontal direction.
 * @param numOfSafeSkips Predetermined number labels to safely skip. This will optimize label
 * measurement as we don't have to measure all the labels.
 * @returns
 */
export function getMaxTextDimensions(
  labels: string[],
  getLabelDims: (label: string, index: number) => TextDimensions,
  isHoriz: boolean,
  numOfSafeSkips?: number
) {
  const { numOfSkips, dims } = skipLabelsUniform(labels, getLabelDims, isHoriz, numOfSafeSkips);
  const tickLabels = [];
  let maxWidth = Number.MIN_VALUE;
  let maxHeight = Number.MIN_VALUE;

  for (let i = 0; i < labels.length; i += numOfSkips + 1) {
    maxWidth = Math.max(maxWidth, dims[i].width);
    maxHeight = Math.max(maxHeight, dims[i].height);
    tickLabels.push({
      index: i,
      label: labels[i],
      labelProps: dims[i]
    });
  }
  return {
    maxHeight,
    maxWidth,
    tickLabels,
    numOfSkips
  };
}

export function updateLabelsDims(
  maxWidth: number,
  maxHeight: number,
  renderedLabelsInfo: any[],
  getAxisPos: (index: number) => number,
  availSpace: Dimension,
  position: AxisPosition,
  isRtl: boolean
) {
  renderedLabelsInfo.forEach((info) => {
    const dim: TextDimensions = info.labelProps;
    let x: number;
    let y: number;
    if (position === 'left') {
      x = availSpace.x + maxWidth;
      y = getAxisPos(info.index);
    } else if (position === 'right') {
      x = availSpace.x + availSpace.width;
      y = getAxisPos(info.index);
    } else if (position === 'top') {
      x = getAxisPos(info.index) - (isRtl ? -1 : 1) * (dim.width / 2);
      y = availSpace.y + maxHeight - dim.height;
    } else {
      x = getAxisPos(info.index);
      y = availSpace.y + availSpace.height - maxHeight / 2 + AXIS_DEFAULTS.labelGap;
    }
    dim.x = x;
    dim.y = y;
  });
}

/**
 * Updates the given available space after labels are rendered.
 * @param availSpace The avaialable space.
 * @param position The position of the axis.
 * @param maxWidth The maximum width of rendered labels.
 * @param maxHeight The maxium height of the rendered labels.
 */
export function updateAvailableSpace(
  availSpace: Dimension,
  position: AxisPosition,
  maxWidth: number,
  maxHeight: number
) {
  if (position == 'left') {
    availSpace.x += maxWidth;
    availSpace.width -= maxWidth;
  } else if (position === 'right') {
    availSpace.x -= maxWidth;
  } else if (position === 'top') {
    availSpace.y += maxHeight;
    availSpace.height -= maxHeight;
  } else {
    availSpace.height -= maxHeight;
  }
}

/**
 * Returns the enclosing rect for the label of given dimension.
 * @param dims The dimension
 * @param axisPosition The position of the axis.
 * @returns
 */
export function getEnclosingRectDims(dims: Dimension, axisPosition: AxisPosition) {
  let x: number;
  let y: number;

  if (axisPosition === 'left') {
    x = dims.x - dims.width;
    y = dims.y - dims.height / 2;
  } else if (axisPosition === 'right') {
    x = dims.x;
    y = dims.y - dims.height / 2;
  } else {
    x = dims.x - dims.width / 2;
    y = dims.y - dims.height / 2;
  }
  const width = dims.width;
  const height = dims.height;

  return { x, y, width, height };
}

/**
 * Retuns the unaligned label dimensions that can be used by skipLabels.
 * @param coord
 * @param label
 * @param labelStyle
 * @param isHoriz
 * @param availSpace
 * @param getTextDimensions
 * @returns
 */
export function getLabelDimensions(
  coord: number,
  label: string,
  labelStyle: Partial<CSSStyleDeclaration>,
  isHoriz: boolean,
  availSpace: Dimension,
  getTextDimensions?: GetTextDimensions
) {
  if (!getTextDimensions) {
    return { width: 0, height: 0, x: 0, y: 0 };
  }

  const gap = AXIS_DEFAULTS.labelGap;
  const { width, height } = getTextDimensions(label, labelStyle as any);
  const labelWidth = isHoriz ? width + gap : width;
  const labelHeight = isHoriz ? height : height + gap;
  return {
    x: isHoriz ? coord - labelWidth / 2 : availSpace.x,
    width: labelWidth,
    height,
    y: isHoriz ? availSpace.y : coord + labelHeight / 2
  };
}
